import { memo, useMemo } from 'react';
import {
  VictoryArea,
  VictoryAxis,
  VictoryBar,
  VictoryBrushContainer,
  VictoryChart,
  VictoryScatter,
  VictoryThemeDefinition,
  VictoryTickStyleObject,
  VictoryVoronoiContainer,
} from 'victory';
import { useIntl } from 'react-intl';
import styled, { useTheme } from 'styled-components';
import { AmplitudeEvent, logAmplitudeEvent } from '@algoan/dashboard/analytics';
import { BankHistory, BankHistoryChartData } from '@algoan/dashboard/model';
import { getAllFirstAndSixteenthOfTheMonthBetweenTwoDates } from '@algoan/dashboard/utils';
import { CustomBar } from './custom-bar';
import { messages } from './bank-history-chart-messages';
import { BalanceTooltip } from './balance-tooltip';
import { BottomTickLabel } from './bottom-tick-label';
import { BarWithTooltip } from './bar-with-tooltip';
import { CustomRange } from './custom-range';
import { CustomGrid } from './custom-grid';
import { CustomLabel } from './custom-label';
import { CustomXTick } from './custom-x-tick';
import { CustomYTick } from './custom-y-tick';
import { getTransactionDaysArray } from './bank-history-chart-utils';
import { RangeStatus, useBankHistorySelection } from './use-bank-history-selection';
import { filterStatementsByTransactionDays } from '@algoan/dashboard/store/slice';
import { useDispatch } from '@algoan/dashboard/store/store';
import { RecursivePartial } from '../../types';
import { timestampToDay } from './utils';
import { CustomSideLabel } from './custom-side-label';

export const defaultChartHeight = 205;
export const chartWidth = 450;
export const chartPadding = { top: 24, bottom: 25, left: 40, right: 40 };
export const chartPaddingTopLegacy = 30;

export interface BankHistoryChartProps {
  bankHistory: BankHistoryChartData;
  bankHistoryTheme: RecursivePartial<VictoryThemeDefinition>;
  enableBalanceTooltip?: boolean;
  enableDifferenceTooltip?: boolean;
  height?: number;
  width?: number;
  variant?: 'primary' | 'legacy';
}

const MemoizedBankHistoryChart: React.FC<BankHistoryChartProps & { width: number }> = memo(
  ({
    bankHistory,
    bankHistoryTheme,
    enableBalanceTooltip = true,
    enableDifferenceTooltip = false,
    height = defaultChartHeight,
    variant = 'primary',
    width,
  }) => {
    const intl = useIntl();
    const { rangeDates } = useBankHistorySelection();
    const theme = useTheme();

    const isPrimary = variant === 'primary';
    const green = isPrimary ? theme.scoreIndicatorColors.positiveValue : theme.legacy.green;
    const red = isPrimary ? theme.scoreIndicatorColors.negativeValue : theme.legacy.red;
    const newChartPadding = { ...chartPadding, top: isPrimary ? chartPadding.top : chartPaddingTopLegacy };

    const timeTickValues = useMemo((): number[] => {
      /**
       * bankHistory.history should not be used because it is empty when no transaction.
       * That is why positiveBalance is used (negativeBalance could be used too).
       */
      const entries = bankHistory?.positiveBalance;
      if (!entries || entries.length === 0) {
        return [];
      }

      const start = new Date(entries[0].date);
      const end = new Date(entries[entries.length - 1].date);

      return getAllFirstAndSixteenthOfTheMonthBetweenTwoDates(start, end).map((d) => d.getTime());
    }, [bankHistory?.positiveBalance]);

    return (
      <>
        <S.GradientContainer>
          <svg>
            <defs>
              <linearGradient id={`positive-gradient-${variant}`} x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor={theme.scoreIndicatorColors.positiveValue} stopOpacity={1} />
                <stop offset="100%" stopColor={theme.scoreIndicatorColors.positiveValue} stopOpacity={0.2} />
              </linearGradient>
              <linearGradient id={`negative-gradient-${variant}`} x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor={theme.scoreIndicatorColors.negativeValue} stopOpacity={0.2} />
                <stop offset="100%" stopColor={theme.scoreIndicatorColors.negativeValue} stopOpacity={1} />
              </linearGradient>
            </defs>
          </svg>
        </S.GradientContainer>
        <VictoryChart
          height={height}
          padding={newChartPadding}
          theme={bankHistoryTheme as VictoryThemeDefinition}
          width={width}
          containerComponent={
            enableBalanceTooltip ? (
              <VictoryVoronoiContainer radius={10} />
            ) : (
              <VictoryBrushContainer
                brushDimension="x"
                allowDrag={false}
                allowDraw={false}
                allowResize={false}
                defaultBrushArea="none"
                brushDomain={{ x: rangeDates }}
                brushStyle={{
                  fill: `${theme.main.deepBlue}`,
                  opacity: 0.05,
                  zIndex: -5,
                  pointerEvents: 'none',
                }}
                brushComponent={<CustomRange data={bankHistory.history} graphWidth={width} />}
                handleStyle={{
                  pointerEvents: 'none',
                  fill: 'transparent',
                }}
              />
            )
          }
        >
          <VictoryAxis
            crossAxis
            tickValues={timeTickValues.length > 0 ? timeTickValues : undefined}
            // we disable the error here because the basic type of the parameter is any
            // @ts-expect-error: Parameter 'timestamp' implicitly has an 'any' type.ts(7006)
            tickFormat={(timestamp) => timestampToDay(timestamp)}
            tickComponent={<CustomYTick isLegacy={!isPrimary} height={height} />}
            tickLabelComponent={<BottomTickLabel chartHeight={height} />}
            style={{ grid: { strokeWidth: isPrimary ? 1 : 0.5 } }}
          />
          <VictoryAxis
            dependentAxis
            tickCount={8}
            tickFormat={(label: number) => intl.formatNumber(label)}
            style={{ ticks: { size: 5 } as VictoryTickStyleObject }}
            crossAxis={false}
            tickComponent={<CustomXTick isLegacy={!isPrimary} />}
            tickLabelComponent={<CustomSideLabel dx={-2} dy={-2} isLegacy={!isPrimary} />}
            label={intl.formatMessage(messages.amount)}
            axisLabelComponent={<CustomLabel isLegacy={!isPrimary} />}
          />
          <VictoryAxis
            dependentAxis
            crossAxis={false}
            gridComponent={<CustomGrid isLegacy={!isPrimary} />}
            offsetX={40}
            orientation="right"
            style={{ ticks: { size: 5 } as VictoryTickStyleObject, tickLabels: { padding: 2 } }}
            tickComponent={<CustomXTick isLegacy={!isPrimary} />}
            tickCount={8}
            tickFormat={(label: number) => intl.formatNumber(label)}
            tickLabelComponent={<CustomSideLabel dx={6} dy={-2} isLegacy={!isPrimary} />}
          />
          <VictoryBar
            dataComponent={enableDifferenceTooltip ? <BarWithTooltip /> : <CustomBar />}
            data={bankHistory.history}
            x="date"
            y="difference"
            style={{
              data: {
                fill: (args) => {
                  const datum: BankHistory = args.datum;
                  return datum.difference > 0 ? green : red;
                },
              },
            }}
            scale="time"
          />
          {enableBalanceTooltip && (
            <VictoryScatter
              data={bankHistory.history}
              // labels should be defined even if the effective label is overriden
              // by labelComponent. Otherwise, the tooltip doesn't appear.
              labels={({ datum }: { datum: BankHistory }) => datum.balance}
              labelComponent={<BalanceTooltip />}
              x="date"
              y="balance"
              size={2.5}
              style={{
                data: {
                  fill: ({ active, datum }) => {
                    const point: BankHistory = datum;
                    if (!active) {
                      return 'transparent';
                    }
                    return point.balance > 0 ? theme.legacy.green : theme.legacy.red;
                  },
                },
              }}
              // disabling pointer events on those charts allows the hover on the previous
              // bar chart to work
              groupComponent={<g className="disable-pointer-events" />}
            />
          )}
          <VictoryArea
            data={bankHistory.negativeBalance}
            x="date"
            y="balance"
            style={{
              data: {
                fill: isPrimary ? `url(#negative-gradient-${variant})` : red,
                stroke: isPrimary ? 'none' : red,
              },
            }}
            scale="time"
            groupComponent={<g className="disable-pointer-events" />}
          />
          <VictoryArea
            data={bankHistory.positiveBalance}
            x="date"
            y="balance"
            style={{
              data: {
                fill: isPrimary ? `url(#positive-gradient-${variant})` : green,
                stroke: isPrimary ? 'none' : green,
              },
            }}
            scale="time"
            groupComponent={<g className="disable-pointer-events" />}
          />
          <VictoryAxis
            crossAxis
            tickFormat={() => ''}
            style={{ grid: { stroke: 'none' }, ticks: { stroke: 'transparent' } }}
          />
        </VictoryChart>
      </>
    );
  },
);
MemoizedBankHistoryChart.displayName = 'MemoizedBankHistoryChart';

export const BankHistoryChart: React.FC<BankHistoryChartProps & { className?: string }> = ({ className, ...props }) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const { endData, originData, rangeStatus, setSelectionEnd, unselectData } = useBankHistorySelection();

  const datesWereSet = rangeStatus === RangeStatus.SELECT_ORIGIN_DATA || rangeStatus === RangeStatus.SELECT_END_DATA;
  const datesWereNotSet =
    rangeStatus === RangeStatus.START_UPDATE_ORIGIN || rangeStatus === RangeStatus.START_UPDATE_END;
  const isStatusIdle = rangeStatus === RangeStatus.IDLE;

  const handleSelectionEnd = (): void => {
    const transactionDays = getTransactionDaysArray({ originData, endData });

    if (datesWereSet) {
      setSelectionEnd();
      dispatch(filterStatementsByTransactionDays(transactionDays));
      transactionDays?.length === 1 &&
        logAmplitudeEvent(AmplitudeEvent.TRANSACTION_SELECT_DATE, {
          date: intl.formatDate(transactionDays[0]),
        });
      transactionDays?.length === 2 &&
        logAmplitudeEvent(AmplitudeEvent.TRANSACTION_SELECT_DATE, {
          dateStart: intl.formatDate(transactionDays[0]),
          dateEnd: intl.formatDate(transactionDays[1]),
        });
    }
    if (datesWereNotSet) unselectData();
  };

  return (
    <S.Container
      onMouseLeave={handleSelectionEnd}
      onMouseUp={handleSelectionEnd}
      className={className}
      $cursor={isStatusIdle}
    >
      <MemoizedBankHistoryChart {...props} width={props.width ?? chartWidth} />
    </S.Container>
  );
};

const S = {
  Container: styled.div<{ $cursor: boolean }>`
    cursor: ${({ $cursor }) => ($cursor ? 'auto' : 'grab')};
    height: 100%;

    .disable-pointer-events {
      pointer-events: none;
    }

    div > svg {
      overflow: visible;
    }
  `,
  GradientContainer: styled.div`
    height: 0px;
  `,
};
